// The Plaid micro-deposit verify flow. For same-day micro-deposits the business must return and
// reopen Plaid Link in update mode to enter the deposit code; the update-mode link_token is minted
// by the backend and embedded in the scoped verify token, so we open Plaid immediately. Completing
// update mode confirms the deposit with Plaid; the backend re-reads Plaid status on the next account
// read and flips isVerified — so no explicit submit call is needed here.
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlaidLink, type PlaidLinkOnExit, type PlaidLinkOnSuccess } from 'react-plaid-link';

import type { EnvironmentId } from '@/config/environments';
import type { InternalAccountLinkClaims } from '@/lib/sessionToken';
import { notifyParent } from '@/lib/embedMessages';

import { Banner } from '@/components/ds';

type Status = 'ready' | 'opening' | 'done' | 'error';

type Props = {
  env: EnvironmentId;
  token: string;
  claims: InternalAccountLinkClaims;
};

export function VerifyPlaidAccount({ claims }: Props) {
  const linkToken = claims.plaid?.linkToken;
  const accountId = claims.accountId;
  if (!linkToken || !accountId) {
    return <Banner kind="critical">This link can’t start verification. Ask your provider to send you a new one.</Banner>;
  }
  return <VerifyRunner linkToken={linkToken} accountId={accountId} />;
}

function VerifyRunner({ linkToken, accountId }: { linkToken: string; accountId: string }) {
  const [status, setStatus] = useState<Status>('ready');
  const [error, setError] = useState('');

  const onSuccess = useCallback<PlaidLinkOnSuccess>(() => {
    setStatus('done');
    setError('');
    notifyParent({ flow: 'verify', kind: 'plaid', status: 'success', accountId });
  }, [accountId]);

  const onExit = useCallback<PlaidLinkOnExit>((err) => {
    setStatus('ready');
    if (err) {
      const message = err.display_message || err.error_message || 'Verification was interrupted.';
      setError(message);
      notifyParent({ flow: 'verify', kind: 'plaid', status: 'error', message });
    } else {
      notifyParent({ flow: 'verify', kind: 'plaid', status: 'cancelled' });
    }
  }, []);

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess, onExit });

  const openPlaid = useCallback(() => {
    setStatus('opening');
    setError('');
    open();
  }, [open]);

  // Open Plaid the moment it's ready instead of waiting for a click — this page is a
  // single-purpose embed reached only via a one-time link, so there's no button. If the user
  // backs out via onExit we notify the parent (above) rather than looping a reopen.
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (ready && !hasAutoOpenedRef.current) {
      hasAutoOpenedRef.current = true;
      openPlaid();
    }
  }, [ready, openPlaid]);

  useEffect(() => {
    if (status === 'opening' && !ready) setStatus('ready');
  }, [ready, status]);

  // Nothing to render except on error — Plaid's own overlay is the only UI here, and on
  // success it already closed itself (the parent was notified via postMessage).
  if (status !== 'error') return null;

  return (
    <Banner kind="critical" style={{ textAlign: 'left' }}>
      {error}
    </Banner>
  );
}
